import { store } from "../../Store";
import { debugLog } from "../../Utils/utils";

class SubscriptionManager {
  constructor(connectionManager) {
    this._conn = connectionManager;
    this.subscriptions = {};
    this._groupMessageHandler = null;
    this._lastGroupId = null;
    this._pendingGroupCallback = null;
    this._settingsHandler = null;
    this._settingsOrgId = null;
    this._pendingSettingsCallback = null;
  }

  /**
   * Called by ConnectionManager.onConnect to restore subscriptions after reconnect.
   * @param {ConnectionManager} conn
   */
  _restoreSubscriptions(conn) {
    // Clear stale subscription references from previous connection
    this.subscriptions._groupSub = null;
    this.subscriptions._groupTopicSub = null;
    this.subscriptions._typingSub = null;
    this.subscriptions._settingsSub = null;

    // Re-subscribe to group messages if we had an active handler
    if (this._pendingGroupCallback) {
      const { userId, groupId, callback } = this._pendingGroupCallback;
      this._pendingGroupCallback = null;
      this.subscribeGroupMessages(callback, userId, groupId);
    } else if (this._groupMessageHandler) {
      const currentUserId = store.getState().auth.user?.id;
      if (currentUserId) {
        this.subscribeGroupMessages(this._groupMessageHandler, currentUserId, this._lastGroupId);
      }
    }

    // Re-subscribe to typing topic if handlers were registered
    if (
      this.subscriptions.typing &&
      typeof this.subscriptions.typing === "function" &&
      !this.subscriptions._typingSub
    ) {
      this.subscriptions._typingSub = conn.client.subscribe(
        `/topic/typing`,
        (msg) => {
          try {
            const payload = JSON.parse(msg.body);
            this.subscriptions.typing(payload);
          } catch (e) {
            this.subscriptions.typing(msg.body);
          }
        }
      );
    }

    // Re-subscribe to settings if handler was registered
    if (this._pendingSettingsCallback) {
      const { orgId, callback } = this._pendingSettingsCallback;
      this._pendingSettingsCallback = null;
      this.subscribeSettings(orgId, callback);
    } else if (this._settingsHandler && this._settingsOrgId) {
      this.subscribeSettings(this._settingsOrgId, this._settingsHandler);
    }
  }

  subscribeTyping(callback) {
    this.subscriptions.typing = callback;
    if (this._conn.client && this._conn.connected && !this.subscriptions._typingSub) {
      const endpoint = `/topic/typing`;
      try {
        this.subscriptions._typingSub = this._conn.client.subscribe(endpoint, (msg) => {
          try {
            const payload = JSON.parse(msg.body);
            callback(payload);
          } catch (e) {
            callback(msg.body);
          }
        });
      } catch (error) {
        debugLog("Failed to subscribe to typing:", error);
      }
    }
  }

  unsubscribeTyping() {
    if (this.subscriptions._typingSub) {
      try {
        this.subscriptions._typingSub.unsubscribe();
      } catch (e) {}
      this.subscriptions._typingSub = null;
    }
    delete this.subscriptions.typing;
  }

  subscribeGroupMessages(callback, userId, groupId) {
    if (!userId) {
      debugLog("User ID is required for group message subscription");
      return;
    }

    this._groupMessageHandler = callback;
    if (groupId) this._lastGroupId = groupId;

    if (!this._conn.client || !this._conn.connected) {
      this._pendingGroupCallback = { userId, groupId, callback };
      return;
    }

    if (!this.subscriptions._groupSub) {
      const userGroupTopic = `/socket-user/${userId}/topic/group`;
      this.subscriptions._groupSub = this._conn.client.subscribe(
        userGroupTopic,
        (msg) => {
          try {
            const payload = JSON.parse(msg.body);
            if (this._groupMessageHandler) {
              this._groupMessageHandler(payload);
            }
          } catch (e) {
            debugLog("Error parsing group message:", e);
          }
        }
      );
    }

    if (groupId && !this.subscriptions._groupTopicSub) {
      const groupBroadcastTopic = `/topic/group/${groupId}`;
      this.subscriptions._groupTopicSub = this._conn.client.subscribe(
        groupBroadcastTopic,
        (msg) => {
          try {
            const payload = JSON.parse(msg.body);
            if (this._groupMessageHandler) {
              this._groupMessageHandler(payload);
            }
          } catch (e) {
            debugLog("Error parsing group broadcast message:", e);
          }
        }
      );
    }
  }

  unsubscribeGroupMessages() {
    if (this.subscriptions._groupSub) {
      this.subscriptions._groupSub.unsubscribe();
      delete this.subscriptions._groupSub;
    }
    if (this.subscriptions._groupTopicSub) {
      this.subscriptions._groupTopicSub.unsubscribe();
      delete this.subscriptions._groupTopicSub;
    }
    delete this._groupMessageHandler;
    delete this._pendingGroupCallback;
  }

  subscribeSettings(orgId, callback) {
    if (!this._conn.client || !this._conn.connected) {
      debugLog("WebSocket not connected, cannot subscribe to settings");
      this._pendingSettingsCallback = { orgId, callback };
      return;
    }

    if (!orgId) {
      debugLog("Org ID is required for settings subscription");
      return;
    }

    this._settingsHandler = callback;
    this._settingsOrgId = orgId;

    if (!this.subscriptions._settingsSub) {
      const settingsTopic = `/topic/org/${orgId}/settings`;
      this.subscriptions._settingsSub = this._conn.client.subscribe(
        settingsTopic,
        (msg) => {
          try {
            const payload = JSON.parse(msg.body);
            if (this._settingsHandler) {
              this._settingsHandler(payload);
            }
          } catch (e) {
            debugLog("Error parsing settings message:", e);
          }
        }
      );
    }
  }

  unsubscribeSettings() {
    if (this.subscriptions._settingsSub) {
      this.subscriptions._settingsSub.unsubscribe();
      delete this.subscriptions._settingsSub;
      delete this._settingsHandler;
    }
  }

  // Called by ConnectionManager.disconnect to clean up all subscriptions
  _unsubscribeAll() {
    if (this.subscriptions._typingSub) {
      try { this.subscriptions._typingSub.unsubscribe(); } catch (e) {}
      this.subscriptions._typingSub = null;
    }
    if (this.subscriptions._groupSub) {
      try { this.subscriptions._groupSub.unsubscribe(); } catch (e) {}
      this.subscriptions._groupSub = null;
    }
    if (this.subscriptions._groupTopicSub) {
      try { this.subscriptions._groupTopicSub.unsubscribe(); } catch (e) {}
      this.subscriptions._groupTopicSub = null;
    }
    if (this.subscriptions._settingsSub) {
      try { this.subscriptions._settingsSub.unsubscribe(); } catch (e) {}
      this.subscriptions._settingsSub = null;
    }
  }
}

export default SubscriptionManager;
